import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FeatureIcon } from '@rossum/ui';
import { IconVariable } from '@rossum/ui/icons/tabler';
import {
  Card,
  CardContent,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { extractVariablesFromQuery } from '../../../../../formulas/FormulaPreview/utils/lookup-fields/extractDependenciesPerQuery';
import { QueryObject, VariablesMap } from './types';

type QueryVariablesCardProps = {
  query: QueryObject;
  variables: VariablesMap;
};

export const QueryVariablesCard = ({
  query,
  variables,
}: QueryVariablesCardProps) => {
  const intl = useIntl();

  const referencedVariables = useMemo(
    () =>
      extractVariablesFromQuery(query)
        .sort()
        .map(name => ({ name, formula: variables[name]?.__formula })),
    [query, variables]
  );

  if (referencedVariables.length === 0) {
    return null;
  }

  return (
    <Card
      variant="outlined"
      sx={{
        // TODO: Define Paper2 in theme and reuse here
        backgroundColor: t =>
          t.palette.mode === 'dark' ? '#1A1F27' : '#F9FAFB',
      }}
    >
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <FeatureIcon content={IconVariable} size="small" color="aurora" />
          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.primary">
              {intl.formatMessage({
                id: 'features.queueSettings.fields.lookup.builtin.complexLookup.variablesUsed',
              })}
            </Typography>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {referencedVariables.map(({ name, formula }) => (
                <Tooltip
                  key={name}
                  placement="top"
                  title={
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {formula ||
                        intl.formatMessage({
                          id: 'features.queueSettings.fields.lookup.builtin.complexLookup.undefinedVariable',
                        })}
                    </Typography>
                  }
                >
                  <Chip label={name} size="small" tabIndex={0} />
                </Tooltip>
              ))}
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};
